import { Link } from "react-router-dom";
import type { NewsArticle } from "@/types/news";
import { formatHeat } from "@/utils/news";

interface ArticleCardProps {
  article: NewsArticle;
  index?: number;
}

function ArticleCard({ article, index }: ArticleCardProps) {
  return (
    <article className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden flex flex-col md:flex-row transition-colors hover:border-primary-300">
      <Link
        to={`/article/${article.id}`}
        className="relative shrink-0 w-full md:w-56 h-48 md:h-auto overflow-hidden cursor-pointer"
        aria-label={article.title}
      >
        <img
          src={article.coverImage}
          alt={article.title}
          title={`${article.title} - 瓜圈速报`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        {typeof index === "number" && (
          <span className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-md bg-foreground-950/75 text-background-50 text-sm font-heading font-bold">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
      </Link>

      <div className="flex-1 p-4 md:p-5 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {article.isBreaking && (
            <span className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] font-bold whitespace-nowrap">
              突发
            </span>
          )}
          <Link
            to={`/category/${article.category}`}
            className="px-2 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
          >
            {article.categoryName}
          </Link>
          <span className="text-[11px] text-foreground-400 whitespace-nowrap">{article.publishedAt}</span>
        </div>

        <h3 className="font-heading text-base md:text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/article/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>

        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{article.summary}</p>

        <div className="mt-auto pt-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {article.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-2 py-1 rounded-md bg-background-100 text-foreground-600 text-[11px]">
                #{tag}
              </span>
            ))}
          </div>
          <span className="flex items-center gap-1.5 text-xs text-primary-600 font-medium whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-fire-line" />
            </span>
            {formatHeat(article.heat)}
          </span>
        </div>
      </div>
    </article>
  );
}

export default ArticleCard;